// 기공비 분석 — 치과 원장용 (2026-08-09 대표 승인)
// 기획서: docs/기획서_치과분석_기공비_재제작_2026-08.md
//
// 2026-10-12 (덴클 1.3.0 WB-1): 계산을 서버 callable getLabCostAnalysis 로 옮겼다 — 웹·앱이 같은 숫자 정본을 쓴다.
//   계약: docs/계약_기공비분석_2026-10-12.md · 서버: functions/lib/lab-cost-analysis.js
//   - 브라우저는 명세서를 직접 읽지 않는다. 응답은 화이트리스트(명세서 id·기공소 uid·원문 환자명 없음)
//   - topPatients 는 includePatients:true 일 때만, 서버가 가린 이름(김○수)으로 준다
//   - 대표 결정(10-12): 치과 대표 전용 — 직원은 서버가 permission-denied {code:'owner-only'}, 라우트·메뉴도 숨김
//   - 진료비(clinicFees)는 여기서 저장만 하고, 비중 계산은 서버가 한다 → 저장 뒤 다시 호출
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase/config';
import { Wallet } from 'lucide-react';
import PageHeader from './common/PageHeader';

const won = (n) => (n || 0).toLocaleString('ko-KR');
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// getLabCostAnalysis 오류 → 화면 1줄 안내 (계약 §4-4). 빈 catch 없음 — 호출부에서 console.error 도 남긴다
const analysisErrorText = (e) => {
    const code = String(e?.code || '');
    const reason = e?.details?.code || '';
    if (reason === 'owner-only') return '기공비 분석은 대표 계정에서만 볼 수 있습니다.';
    if (code.includes('not-found')) return '기공비 분석 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 열어 주세요.';
    if (code.includes('resource-exhausted')) return '짧은 시간에 너무 자주 조회했습니다. 1분쯤 뒤에 다시 시도해 주세요.';
    if (code.includes('unauthenticated')) return '로그인이 필요합니다. 다시 로그인해 주세요.';
    if (code.includes('permission-denied') || code.includes('invalid-argument')) {
        // 서버가 준 한국어 사유(비치과·승인 대기·기간 오류)를 그대로
        return /[가-힣]/.test(e?.message || '') ? e.message : '이 계정으로는 기공비 분석을 볼 수 없습니다.';
    }
    return '기공비 분석을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
};

function LabCostAnalysis({ user }) {
    // 📊 기공비 분석 — 서버 callable getLabCostAnalysis 응답 (2026-10-12)
    const [loading, setLoading] = useState(true);
    const [analysis, setAnalysis] = useState(null);     // 응답 전체 (계약 §2)
    const [months, setMonths] = useState(6);            // 조회 기간(개월) — 서버 허용 3·6·12
    const [error, setError] = useState('');
    const reqSeq = useRef(0);                           // 기간 칩 연타 시 늦게 온 옛 응답 무시
    // 💰 이번 달 예상 기공비 — 서버 callable getExpectedLabCost 응답 (2026-10-07, 기획서_치과_예상기공비 §3 · 법무 §4-2)
    //   기존 카드 A(revenueEstimates 직접 조회)·카드 B(실적 단가 추정)를 이 1카드로 통합. 치과는 문서를 직접 읽지 않고 서버가 화이트리스트만 응답.
    const [costMonth, setCostMonth] = useState('this');   // 'this' | 'prev'
    const [cost, setCost] = useState(null);               // 응답 {monthKey,isCurrentMonth,labs,errors,disclaimer}
    const [costLoading, setCostLoading] = useState(true);
    const [costError, setCostError] = useState('');
    // 우리 진료비(수가) — 품목 표준명칭 → 치아 1개당 환자 수납가 (2026-08-09 대표 지시)
    const [fees, setFees] = useState({});
    const [feeDraft, setFeeDraft] = useState({});
    const [feeSaving, setFeeSaving] = useState(false);
    const [feeMsg, setFeeMsg] = useState('');
    const [feeOpen, setFeeOpen] = useState(false);

    const uid = user?.companyId || user?.uid;
    const isClinic = ['clinic', 'dental', '치과'].includes(user?.businessType || user?.companyBusinessType);
    // 서버 requireClinicOwner 와 같은 판정 — 직원이면 호출하지 않고 안내만
    const isStaff = user?.userType === 'staff' || (!!user?.companyId && user.companyId !== user?.uid);

    const fetchAnalysis = useCallback(async () => {
        if (!uid || !isClinic || isStaff) { setLoading(false); return; }
        const seq = ++reqSeq.current;
        setLoading(true);
        setError('');
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'getLabCostAnalysis');
            const res = await fn({ months, includePatients: true });   // 웹 전용 옵션 — 가린 환자 이름 상위 5
            if (seq !== reqSeq.current) return;
            setAnalysis(res?.data || null);
        } catch (e) {
            if (seq !== reqSeq.current) return;
            console.error('[기공비 분석] 서버 조회 실패:', e?.code || '', e?.details?.code || '', e?.message || '');
            setAnalysis(null);
            setError(analysisErrorText(e));
        } finally {
            if (seq === reqSeq.current) setLoading(false);
        }
    }, [uid, isClinic, isStaff, months]);

    useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

    // 💰 이번 달(또는 지난달) 예상 기공비 — 서버 callable. 기공소별 {shared, expected, confirmed}.
    //   호출자(치과 대표·직원)는 서버가 users 문서로 판정한다. 실패는 화면에 1줄로 알린다(조용한 실패 금지) —
    //   구 서버(함수 미배포)에서는 not-found 로 떨어지므로 그 경우 안내 문구를 따로 둔다.
    const fetchCost = useCallback(async () => {
        if (!uid) return;
        setCostLoading(true);
        setCostError('');
        try {
            const now = new Date();
            const target = costMonth === 'prev' ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : now;
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'getExpectedLabCost');
            const res = await fn({ monthKey: monthKey(target) });
            setCost(res?.data || null);
        } catch (e) {
            console.error('[기공비 분석] 예상 기공비(서버) 불러오기 실패:', e);
            const code = e?.code || '';
            setCost(null);
            setCostError(code.includes('not-found')
                ? '예상 기공비 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 열어 주세요.'
                : `예상 기공비를 불러오지 못했습니다${e?.message ? ` (${e.message})` : ''}. 잠시 후 다시 시도해 주세요.`);
        } finally {
            setCostLoading(false);
        }
    }, [uid, costMonth]);
    useEffect(() => { fetchCost(); }, [fetchCost]);

    // 진료비 표 불러오기 — 문서 키는 로그인한 대표 uid (rules: 본인만 읽고 쓴다)
    //   입력칸 초기값과 기공소 비교의 "진료비의 N%" 에 쓴다. 비중(margin) 계산은 서버가 같은 문서를 읽어 한다
    const feeDocId = auth.currentUser?.uid;
    useEffect(() => {
        if (!feeDocId) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'clinicFees', feeDocId));
                const f = (snap.exists() && snap.data().fees) || {};
                setFees(f);
                setFeeDraft(f);
            } catch (e) {
                console.error('[기공비 분석] 진료비 불러오기 실패:', e);   // 조용히 넘기지 않는다
            }
        })();
    }, [feeDocId]);

    const saveFees = async () => {
        if (!feeDocId || feeSaving) return;
        setFeeSaving(true);
        setFeeMsg('');
        try {
            // 빈칸·0은 저장하지 않는다 (입력 안 한 품목과 0원을 구분)
            const clean = {};
            Object.entries(feeDraft).forEach(([k, v]) => {
                const n = Number(String(v).replace(/[^\d]/g, ''));
                if (n > 0) clean[k] = n;
            });
            await setDoc(doc(db, 'clinicFees', feeDocId), { fees: clean, updatedAt: new Date() }, { merge: true });
            setFees(clean);
            setFeeDraft(clean);
            setFeeMsg('저장했습니다.');
        } catch (e) {
            console.error('[기공비 분석] 진료비 저장 실패:', e);
            setFeeMsg(`저장하지 못했습니다: ${e.message}`);
            setFeeSaving(false);
            return;
        }
        setFeeSaving(false);
        // 비중은 품목 전체 기준이라 서버가 다시 계산해야 맞다 (계약 §4-2)
        fetchAnalysis();
    };

    // ── 서버 응답 → 화면 데이터 (계약 §2 대조표) ──
    const A = analysis || {};
    const rowCount = A.rowCount || 0;
    const summary = A.summary || { total: 0, teeth: 0, cases: 0, remake: 0, labs: 0, thisMonth: 0, prevMonth: 0, diffPct: null, perCase: 0, perTooth: 0, remakePct: 0 };
    const trend = { arr: Array.isArray(A.trend) ? A.trend : [], max: A.trendMax || 1 };
    const byName = { arr: Array.isArray(A.byName) ? A.byName : [], max: A.byNameMax || 1 };
    const margin = A.margin || { lines: [], revenue: 0, labCost: 0, share: 0, covered: 0, totalTeeth: 0, coverPct: 0, hasAny: false };
    const marginLines = Array.isArray(margin.lines) ? margin.lines : [];
    const priceHikes = Array.isArray(A.priceHikes) ? A.priceHikes : [];
    const compare = Array.isArray(A.compare) ? A.compare : [];
    const settlement = A.settlement || { count: 0, pending: { n: 0, amount: 0 }, settled: { n: 0, amount: 0 }, disputed: { n: 0, amount: 0 } };
    const topPatients = Array.isArray(A.topPatients) ? A.topPatients : [];

    // 비중 색 — 낮을수록 좋다
    const shareColor = (v) => (v <= 25 ? '#006300' : v <= 40 ? '#8a6100' : '#d03b3b');

    if (!isClinic) {
        return (
            <div style={S.page}>
                <PageHeader icon={Wallet} title="기공비 분석" />
                <div style={S.empty}>이 화면은 치과 계정에서만 볼 수 있습니다.</div>
            </div>
        );
    }

    if (isStaff) {
        return (
            <div style={S.page}>
                <PageHeader icon={Wallet} title="기공비 분석" />
                <div style={S.empty}>기공비 분석은 대표 계정에서만 볼 수 있습니다.</div>
            </div>
        );
    }

    if (loading && !analysis && !error) return (
        <div style={S.page}>
            <PageHeader icon={Wallet} title="기공비 분석" />
            <div style={S.empty}>불러오는 중…</div>
        </div>
    );

    return (
        <div style={S.page}>
            <PageHeader icon={Wallet} title="기공비 분석" />

            <div style={S.topbar}>
                <div style={S.topText}>
                    받은 거래명세서(발행 완료분) 기준 · 최근 {months}개월
                    {A.range?.from && A.range?.to ? ` (${A.range.from} ~ ${A.range.to})` : ''}
                    {loading && analysis ? ' · 다시 계산하는 중…' : ''}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {[3, 6, 12].map(m => (
                        <button key={m} onClick={() => setMonths(m)} disabled={loading}
                            style={{ ...S.chip, ...(months === m ? S.chipOn : {}) }}>{m}개월</button>
                    ))}
                </div>
            </div>

            {error && <div style={S.error}>{error}</div>}

            {/* 💰 이번 달 예상 기공비 — 서버 getExpectedLabCost (2026-10-07). 기존 카드 A(revenueEstimates)·카드 B(실적 단가 추정) 통합.
                받은 명세서가 없어도(rows 비어도) 보이도록 상단에 둔다. ☆ 하단 문구는 서버 disclaimer 그대로(법무 §4-2). */}
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <h3 style={S.h3}>{costMonth === 'prev' ? '지난달 예상 기공비' : '이번 달 예상 기공비'}</h3>
                        <p style={S.cap}>
                            거래 기공소가 등록한 <b>우리 치과 단가표</b>로 {costMonth === 'prev' ? '지난달' : '이번 달'} 의뢰서를 <b>기공소 검토 없이 자동 계산한</b> 값입니다.
                            명세서로 확정된 금액은 <b>확정</b>, 아직 명세서가 없는 몫은 <b>아직 예상</b>으로 나눠 보여줍니다.
                            {/* ☆ "기공소 검토 없이 자동 계산" = 법무검토_치과_기간예상명세서 §3 C-1 추가 문장 (2026-10-07) */}
                            {costMonth === 'prev' && ' 지난달도 현재 단가 기준입니다.'}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setCostMonth('this')} style={{ ...S.chip, ...(costMonth === 'this' ? S.chipOn : {}) }}>이번 달</button>
                        <button onClick={() => setCostMonth('prev')} style={{ ...S.chip, ...(costMonth === 'prev' ? S.chipOn : {}) }}>지난달</button>
                    </div>
                </div>

                {costLoading && <div style={S.sub}>예상 기공비를 계산하는 중…</div>}
                {!costLoading && costError && <div style={{ ...S.error, marginTop: 6, marginBottom: 0 }}>{costError}</div>}

                {!costLoading && !costError && cost && (() => {
                    const labs = Array.isArray(cost.labs) ? cost.labs : [];
                    const errors = Array.isArray(cost.errors) ? cost.errors : [];
                    const shown = labs.filter(l => l.shared && l.expected && l.confirmed);
                    const sumConfirmed = shown.reduce((a, l) => a + (l.confirmed.amount || 0), 0);
                    const sumPending = shown.reduce((a, l) => a + Math.max(0, (l.expected.amount || 0) - (l.confirmed.expectedPortion || 0)), 0);
                    const sumExpected = shown.reduce((a, l) => a + (l.expected.amount || 0), 0);
                    const anyNoPrice = shown.some(l => !l.expected.hasPrice);
                    if (labs.length === 0) {
                        return <div style={S.sub}>연결된 기공소가 없습니다. 기공소와 연결되면 여기에 예상 기공비가 표시됩니다.</div>;
                    }
                    return (
                        <>
                            {/* 연결 기공소 상한(limited) — 서버가 앞 max곳만 계산했음을 알린다. cached 는 표시하지 않는다 */}
                            {cost.limited && cost.limited.total > cost.limited.max && (
                                <div style={{ ...S.sub, marginTop: 0, marginBottom: 8, color: '#8a6100' }}>
                                    연결 기공소 {cost.limited.total}곳 중 {cost.limited.max}곳까지 표시합니다.
                                </div>
                            )}
                            {/* 기공소별 줄: 확정 / 아직 예상 / 합계(예상) · 건수·치아·단가 미등록 수 */}
                            <div style={S.costHead}>
                                <span>기공소</span>
                                <span style={{ textAlign: 'right' }}>확정(명세서)</span>
                                <span style={{ textAlign: 'right' }}>아직 예상</span>
                                <span style={{ textAlign: 'right' }}>합계(예상)</span>
                            </div>
                            {labs.map(l => {
                                if (!l.shared) {
                                    // 법무 §4-2: "제공하지 않음"이 아니라 "표시하지 않습니다" — 끄기 권리 행사에 압박을 주지 않는다
                                    return (
                                        <div key={l.labId} style={S.costRow}>
                                            <span style={S.costLab}>{l.labName || '기공소'}</span>
                                            <span style={{ gridColumn: '2 / span 3', textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>이 기공소는 예상치를 표시하지 않습니다</span>
                                        </div>
                                    );
                                }
                                if (!l.expected || !l.confirmed) {
                                    return (
                                        <div key={l.labId} style={S.costRow}>
                                            <span style={S.costLab}>{l.labName || '기공소'}</span>
                                            <span style={{ gridColumn: '2 / span 3', textAlign: 'right', fontSize: 12, color: '#8a2020' }}>계산하지 못했습니다 — 잠시 후 다시 시도해 주세요</span>
                                        </div>
                                    );
                                }
                                const ex = l.expected, cf = l.confirmed;
                                const pending = Math.max(0, (ex.amount || 0) - (cf.expectedPortion || 0));
                                return (
                                    <div key={l.labId} style={S.costRow}>
                                        <span style={S.costLab}>
                                            <b style={{ color: '#0f172a' }}>{l.labName || '기공소'}</b>
                                            <div style={S.costMeta}>
                                                의뢰 {ex.count || 0}건 · 치아 {ex.teeth || 0}개
                                                {cf.count > 0 ? ` · 명세서 ${cf.statements || 0}건(의뢰 ${cf.count}건 확정)` : ''}
                                                {ex.unpricedItems > 0 ? ` · 단가 미등록 품목 ${ex.unpricedItems}개` : ''}
                                            </div>
                                            {/* 거래처가 이름으로만 매칭된(인증 식별자 없는) 의뢰는 서버가 제외 — 건수만 내려온다(보안 P1-1) */}
                                            {l.unmatchedByName?.count > 0 && (
                                                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>거래처 자동 매칭 안 된 {l.unmatchedByName.count}건 제외</div>
                                            )}
                                        </span>
                                        <span style={{ textAlign: 'right', color: '#006300', fontWeight: 700 }}>{cf.amount > 0 ? `${won(cf.amount)}원` : '-'}</span>
                                        <span style={{ textAlign: 'right', color: '#334155', fontWeight: 700 }}>{ex.hasPrice ? `${won(pending)}원` : '—'}</span>
                                        <span style={{ textAlign: 'right', fontWeight: 800 }}>
                                            {/* 단가표 없는 기공소는 0원을 예상치로 오인하지 않게 "—" (법무 §4-2) */}
                                            {ex.hasPrice ? `${won(ex.amount || 0)}원` : <span title="이 기공소는 우리 치과 단가표를 아직 등록하지 않았습니다">예상 —</span>}
                                        </span>
                                    </div>
                                );
                            })}
                            {shown.length > 0 && (
                                <div style={{ ...S.costRow, borderBottom: 'none', paddingBottom: 0 }}>
                                    <span style={{ fontWeight: 700 }}>합계{anyNoPrice ? ' (단가표 있는 기공소만)' : ''}</span>
                                    <span style={{ textAlign: 'right', color: '#006300', fontWeight: 800 }}>{won(sumConfirmed)}원</span>
                                    <span style={{ textAlign: 'right', fontWeight: 800 }}>{won(sumPending)}원</span>
                                    <span style={{ textAlign: 'right', fontWeight: 800, fontSize: 15 }}>{won(sumExpected)}원</span>
                                </div>
                            )}
                            {errors.length > 0 && (
                                <div style={{ ...S.error, marginTop: 10, marginBottom: 0 }}>
                                    {errors.map(er => er.labName || er.labId).join(', ')} — 계산하지 못한 기공소가 있어 합계에 빠져 있습니다. 잠시 후 다시 시도해 주세요.
                                </div>
                            )}
                            <div style={S.sub}>
                                {/* ☆ 서버 disclaimer 그대로 — 바꾸려면 functions/lib/expected-lab-cost.js DISCLAIMER 와 함께 */}
                                <b>{cost.disclaimer || '기공소가 등록한 단가표와 이번 달 의뢰서로 자동 계산한 참고용 예상치입니다. 견적서·청구서가 아니며, 실제 금액은 기공소가 발행한 거래명세서 기준입니다.'}</b>
                                <br />예상은 현재 단가 기준이며 리메이크·수기 조정은 명세서에서 확정됩니다. 단가 미등록 품목은 예상에 포함되지 않습니다.
                                {cost.range?.from && cost.range?.to ? ` 집계 기간: ${cost.range.from} ~ ${cost.range.to}.` : ''}
                            </div>
                        </>
                    );
                })()}
            </div>

            {/* 서버 조회 실패면 아래 분석 칸은 그리지 않는다(위 error 1줄로 안내). 빈 화면 = rowCount === 0 */}
            {!analysis ? null : rowCount === 0 ? (
                <div style={S.empty}>
                    이 기간에 받은 거래명세서가 없습니다.<br />
                    기공소가 명세서를 발행하면 여기에 자동으로 쌓입니다.
                </div>
            ) : (
                <>
                    {/* 1. 타일 */}
                    <div style={S.tiles}>
                        <div style={S.tile}>
                            <div style={S.tk}>이번 달 기공비</div>
                            <div style={S.tv}>{won(summary.thisMonth)}<small style={S.tu}>원</small></div>
                            <div style={{ ...S.td, color: summary.diffPct == null ? '#64748b' : summary.diffPct > 0 ? '#d03b3b' : '#006300' }}>
                                {summary.diffPct == null ? '전월 자료 없음'
                                    : `${summary.diffPct > 0 ? '▲' : '▼'} ${Math.abs(summary.diffPct).toFixed(1)}% · 전월 ${won(summary.prevMonth)}원`}
                            </div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>기간 합계</div>
                            <div style={S.tv}>{won(summary.total)}<small style={S.tu}>원</small></div>
                            <div style={S.td}>{summary.cases}건 · 기공소 {summary.labs}곳</div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>건당 평균</div>
                            <div style={S.tv}>{won(summary.perCase)}<small style={S.tu}>원</small></div>
                            <div style={S.td}>총 {summary.cases}건</div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>치아당 평균</div>
                            <div style={S.tv}>{won(summary.perTooth)}<small style={S.tu}>원</small></div>
                            <div style={S.td}>총 {summary.teeth}개</div>
                        </div>
                    </div>

                    {/* 2. 월별 추이 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>월별 기공비</h3>
                        <p style={S.cap}>진한 막대가 이번 달입니다.</p>
                        <div style={S.trend}>
                            {trend.arr.map((t, i) => (
                                <div key={t.key} style={S.tcol}>
                                    <div style={S.tval}>{t.amount ? `${Math.round(t.amount / 10000).toLocaleString()}만` : '-'}</div>
                                    <div style={{
                                        ...S.tbar,
                                        height: `${Math.max(4, (t.amount / trend.max) * 100)}%`,
                                        background: i === trend.arr.length - 1 ? '#2a78d6' : '#86b6ef',
                                    }} />
                                    <div style={S.tlab}>{t.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 2-2. 진료비 대비 마진 (우리 진료비를 넣어야 나온다) */}
                    <div style={S.card}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                                <h3 style={S.h3}>진료비 대비 기공료 비중</h3>
                                <p style={S.cap}>
                                    환자에게 받는 <b>치아 1개당 진료비</b>를 넣으면, 그중 기공료가 몇 %를 차지하는지 보여줍니다.
                                    입력한 값은 원장님만 볼 수 있고 기공소에는 보이지 않습니다.
                                </p>
                            </div>
                            <button onClick={() => setFeeOpen(o => !o)} style={{ ...S.chip, ...(feeOpen ? S.chipOn : {}) }}>
                                {feeOpen ? '입력 닫기' : '진료비 입력'}
                            </button>
                        </div>

                        {margin.hasAny ? (
                            <>
                                <div style={S.marginRow}>
                                    <div style={S.mBox}>
                                        <div style={S.tk}>진료비 합계(예상)</div>
                                        <div style={{ ...S.tv, fontSize: 21 }}>{won(margin.revenue)}<small style={S.tu}>원</small></div>
                                    </div>
                                    <div style={S.mBox}>
                                        <div style={S.tk}>그중 기공료</div>
                                        <div style={{ ...S.tv, fontSize: 21 }}>{won(margin.labCost)}<small style={S.tu}>원</small></div>
                                    </div>
                                    <div style={{ ...S.mBox, background: '#f8fafc' }}>
                                        <div style={S.tk}>기공료 비중</div>
                                        <div style={{ ...S.tv, fontSize: 21, color: shareColor(margin.share) }}>
                                            {margin.share.toFixed(1)}<small style={S.tu}>%</small>
                                        </div>
                                    </div>
                                </div>
                                <div style={S.sub}>
                                    진료비를 넣은 품목 기준입니다 — 이 기간 치아 {margin.totalTeeth}개 중 <b>{margin.covered}개({margin.coverPct.toFixed(0)}%)</b>가 계산에 들어갔습니다.
                                    {margin.coverPct < 100 && ' 나머지 품목도 넣으면 더 정확해집니다.'}
                                </div>

                                <div style={{ marginTop: 14 }}>
                                    {marginLines.slice(0, 10).map(l => (
                                        <div key={l.name} style={S.mLine}>
                                            <div style={S.mName}>{l.name}</div>
                                            <div style={S.mNums}>진료비 {won(l.fee)}원 · 기공료 {won(l.perTooth)}원 (개당)</div>
                                            <div style={{ ...S.mRate, color: shareColor(l.share) }}>{l.share.toFixed(0)}%</div>
                                            <div style={S.mTotal}>{l.teeth}개 · 기공료 {won(l.labCost)}원</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div style={S.sub}>
                                아직 진료비를 넣지 않으셨습니다. <b>[진료비 입력]</b>을 눌러 자주 하는 보철부터 한두 개만 넣어보셔도 됩니다.
                            </div>
                        )}

                        {feeOpen && (
                            <div style={S.feeBox}>
                                <div style={S.feeHead}>
                                    <span>품목</span>
                                    <span style={{ textAlign: 'right' }}>치아당 기공비</span>
                                    <span style={{ textAlign: 'right' }}>우리 진료비(개당)</span>
                                    <span style={{ textAlign: 'right' }}>기공료 비중</span>
                                </div>
                                {byName.arr.slice(0, 20).map(b => {
                                    const v = feeDraft[b.name] ?? '';
                                    const n = Number(String(v).replace(/[^\d]/g, '')) || 0;
                                    const share = n > 0 ? (b.perTooth / n) * 100 : null;
                                    return (
                                        <div key={b.name} style={S.feeRow}>
                                            <span style={S.feeName}>{b.name}</span>
                                            <span style={S.feeCost}>{won(b.perTooth)}원</span>
                                            <span style={{ textAlign: 'right' }}>
                                                <input
                                                    value={v}
                                                    onChange={(e) => setFeeDraft(d => ({ ...d, [b.name]: e.target.value.replace(/[^\d]/g, '') }))}
                                                    placeholder="0"
                                                    inputMode="numeric"
                                                    style={S.feeInput}
                                                />
                                            </span>
                                            <span style={{ ...S.feeDiff, color: share == null ? '#94a3b8' : shareColor(share) }}>
                                                {share == null ? '-' : `${share.toFixed(0)}%`}
                                            </span>
                                        </div>
                                    );
                                })}
                                <div style={S.feeFoot}>
                                    <span style={S.sub}>{feeMsg}</span>
                                    <button onClick={saveFees} disabled={feeSaving} style={S.saveBtn}>
                                        {feeSaving ? '저장 중…' : '진료비 저장'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 2-3. 단가 인상 감지 — 이 화면에서 가장 값나가는 칸 */}
                    {priceHikes.length > 0 && (
                        <div style={S.card}>
                            <h3 style={S.h3}>단가가 달라진 품목</h3>
                            <p style={S.cap}>
                                조회 기간을 반으로 나눠 앞뒤 <b>치아당 단가</b>를 견줍니다. 3% 이상 달라진 것만 나옵니다 —
                                기공소가 조용히 올린 것도, 내려준 것도 함께 보입니다.
                            </p>
                            {priceHikes.slice(0, 8).map((h, i) => {
                                const up = h.diff > 0;
                                return (
                                    <div key={i} style={S.hikeRow}>
                                        <div style={S.hikeName}>
                                            {h.name}
                                            <div style={S.hikeLab}>{h.lab}</div>
                                        </div>
                                        <div style={S.hikeNums}>
                                            {won(h.before)}원 → <b style={{ color: up ? '#d03b3b' : '#006300' }}>{won(h.after)}원</b>
                                            <span style={{ ...S.hikePct, color: up ? '#d03b3b' : '#006300' }}>
                                                {up ? '▲' : '▼'} {Math.abs(h.pct).toFixed(0)}%
                                            </span>
                                        </div>
                                        <div style={{ ...S.hikeExtra, color: up ? '#d03b3b' : '#006300' }}>
                                            {up ? '+' : ''}{won(h.extra)}원
                                            <div style={S.hikeSub}>{up ? '더 나감' : '아낌'} · 치아 {h.curTeeth}개</div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={S.sub}>
                                기간을 길게 잡을수록(1년) 흐름이 뚜렷해집니다. 물량이 적은 품목은 한두 건에 크게 흔들리니 건수도 함께 보세요.
                            </div>
                        </div>
                    )}

                    {/* 3. 기공소별 같은 품목 단가 비교 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>기공소별 단가 비교</h3>
                        <p style={S.cap}>
                            기공소마다 다르게 적어 보낸 품목명을 같은 이름으로 묶어, <b>치아 1개당 단가</b>로 맞춰 비교합니다.
                            두 곳 이상에서 받은 품목만 나옵니다. 내부 참고용입니다.
                        </p>
                        {compare.length === 0 ? (
                            <div style={S.sub}>같은 품목을 두 곳 이상에서 받은 기록이 아직 없습니다.</div>
                        ) : compare.slice(0, 6).map(c => (
                            <div key={c.name} style={S.cmpBlock}>
                                <div style={S.cmpHead}>
                                    <b>{c.name}</b>
                                    {c.gap > 0 && (
                                        <span style={S.cmpGap}>
                                            개당 {won(c.gap)}원 차이 · 이 기간 {won(c.saving)}원
                                        </span>
                                    )}
                                </div>
                                {c.labs.map((l, idx) => (
                                    <div key={l.lab} style={S.crow}>
                                        <div style={S.clab}>{l.lab}</div>
                                        <div style={S.ctrack}>
                                            <div style={{
                                                ...S.cfill,
                                                width: `${Math.max(6, (l.perTooth / c.max) * 100)}%`,
                                                background: idx === 0 ? '#1baf7a' : idx === c.labs.length - 1 ? '#d03b3b' : '#2a78d6',
                                            }} />
                                        </div>
                                        <div style={{
                                            ...S.camt,
                                            color: idx === 0 ? '#006300' : idx === c.labs.length - 1 ? '#d03b3b' : '#334155',
                                        }}>
                                            {won(l.perTooth)}원
                                            <span style={S.ccases}> · {l.cases}건</span>
                                            {/* 진료비를 넣은 품목이면 이 기공소를 쓸 때의 마진율까지 (2026-08-09) */}
                                            {fees[c.name] > 0 && (
                                                <span style={S.ccases}> · 진료비의 {((l.perTooth / fees[c.name]) * 100).toFixed(0)}%</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* 4. 품목별 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>품목별</h3>
                        <p style={S.cap}>금액이 큰 순서입니다. 오른쪽은 건수와 치아당 단가입니다.</p>
                        {byName.arr.slice(0, 10).map(b => (
                            <div key={b.name} style={S.brow}>
                                <div style={S.blab}>{b.name}</div>
                                <div style={S.btrack}>
                                    <div style={{ ...S.bfill, width: `${Math.max(4, (b.amount / byName.max) * 100)}%` }}>
                                        <span style={S.bamt}>{won(b.amount)}원</span>
                                    </div>
                                </div>
                                <div style={S.brt}><b>{b.cases}건</b> · {won(b.perTooth)}원/개</div>
                            </div>
                        ))}
                    </div>

                    {/* 5. 재제작 비용 + 정산 상태 */}
                    <div style={S.two}>
                        <div style={{ ...S.card, flex: 1, marginBottom: 0 }}>
                            <h3 style={S.h3}>재제작에 나간 기공비</h3>
                            <p style={S.cap}>기공소가 명세서에 [리메이크]로 표시한 항목의 합계입니다.</p>
                            <div style={S.remakeBox}>
                                <div style={S.remakeAmt}>{won(summary.remake)}<small style={S.tu}>원</small></div>
                                <div style={S.remakePct}>전체 기공비의 {summary.remakePct.toFixed(1)}%</div>
                            </div>
                            <div style={S.sub}>왜 다시 만들었는지는 <b>재제작 분석</b>에서 봅니다.</div>
                        </div>
                        <div style={{ ...S.card, flex: 1, marginBottom: 0 }}>
                            <h3 style={S.h3}>정산 상태</h3>
                            <p style={S.cap}>명세서 {settlement.count}건 기준입니다.</p>
                            <div style={S.srow}><span>확인 전</span><b>{settlement.pending.n}건 · {won(settlement.pending.amount)}원</b></div>
                            <div style={S.srow}><span>정산 완료</span><b style={{ color: '#006300' }}>{settlement.settled.n}건 · {won(settlement.settled.amount)}원</b></div>
                            <div style={S.srow}><span>이의 제기</span><b style={{ color: settlement.disputed.n ? '#d03b3b' : '#94a3b8' }}>{settlement.disputed.n}건 · {won(settlement.disputed.amount)}원</b></div>
                        </div>
                    </div>

                    {/* 6. 환자별 상위 */}
                    {topPatients.length > 0 && (
                        <div style={S.card}>
                            <h3 style={S.h3}>기공비가 큰 환자</h3>
                            <p style={S.cap}>이름은 가려서 표시합니다.</p>
                            {/* 이름은 서버가 가려서 준다(김○수) — 가린 이름끼리 겹칠 수 있어 key 에 순번을 붙인다 */}
                            {topPatients.map((p, i) => (
                                <div key={`${p.name}-${i}`} style={S.srow}>
                                    <span>{p.name}</span>
                                    <b>{won(p.amount)}원 · {p.cases}건</b>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={S.note}>
                        <b>집계 기준</b> — 발행 완료된 거래명세서만 셉니다(임시저장 제외). 덴탈클러스터를 쓰지 않는 기공소와의 거래,
                        종이로 받은 명세서는 여기에 잡히지 않습니다. 화면의 숫자는 <b>받은 명세서 기준</b>입니다.
                    </div>
                    <div style={S.note}>
                        <b>단가 비교 주의</b> — 같은 이름의 품목이라도 재료 등급·작업 범위가 다를 수 있습니다.
                        기공소와 이야기하실 때 참고 자료로 쓰시고, 이 화면을 외부에 공개하거나 광고에 쓰지 마세요.
                    </div>
                </>
            )}
        </div>
    );
}

const S = {
    page: { padding: 20, maxWidth: 1120, margin: '0 auto' },
    topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '4px 0 16px', flexWrap: 'wrap' },
    topText: { fontSize: 12.5, color: '#64748b' },
    chip: { padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    chipOn: { background: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    error: { background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '11px 13px', fontSize: 13, marginBottom: 12 },
    empty: { border: '1px dashed #e2e8f0', borderRadius: 9, padding: 26, textAlign: 'center', fontSize: 13, color: '#64748b', lineHeight: 1.8, background: '#fbfcfd' },

    tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 },
    tile: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' },
    tk: { fontSize: 11, color: '#64748b', fontWeight: 600 },
    tv: { fontSize: 25, fontWeight: 800, marginTop: 5, lineHeight: 1, letterSpacing: '-0.5px', color: '#0f172a' },
    tu: { fontSize: 13, fontWeight: 700, color: '#64748b', marginLeft: 2 },
    td: { fontSize: 11, marginTop: 6, fontWeight: 700, color: '#64748b' },

    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 14 },
    h3: { fontSize: 13, margin: '0 0 3px', fontWeight: 700, color: '#0f172a' },
    cap: { fontSize: 11.5, color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 },
    sub: { fontSize: 12, color: '#64748b', marginTop: 10, lineHeight: 1.6 },
    two: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 },

    trend: { display: 'flex', alignItems: 'flex-end', gap: 12, height: 150, padding: '0 4px' },
    tcol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' },
    tbar: { width: '100%', maxWidth: 46, borderRadius: '4px 4px 0 0' },
    tval: { fontSize: 10.5, color: '#64748b' },
    tlab: { fontSize: 11, color: '#898781' },

    brow: { display: 'grid', gridTemplateColumns: '130px 1fr 140px', alignItems: 'center', gap: 10, marginBottom: 9 },
    blab: { fontSize: 12.5, color: '#334155', textAlign: 'right' },
    btrack: { height: 20, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
    bfill: { height: '100%', borderRadius: 4, background: '#2a78d6', display: 'flex', alignItems: 'center', paddingLeft: 8 },
    bamt: { fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },
    brt: { fontSize: 12, color: '#64748b', textAlign: 'right' },

    cmpBlock: { border: '1px solid #e2e8f0', borderRadius: 9, padding: '12px 13px', background: '#fbfcfd', marginBottom: 10 },
    cmpHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12.5, marginBottom: 10, flexWrap: 'wrap' },
    cmpGap: { fontSize: 11.5, fontWeight: 700, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 6, padding: '3px 8px' },
    crow: { display: 'grid', gridTemplateColumns: '130px 1fr 130px', alignItems: 'center', gap: 9, marginBottom: 7 },
    clab: { fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    ctrack: { height: 18, background: '#eef1f5', borderRadius: 4, overflow: 'hidden' },
    cfill: { height: '100%', borderRadius: 4 },
    camt: { fontSize: 12, fontWeight: 700, textAlign: 'right' },
    ccases: { fontWeight: 500, color: '#94a3b8' },

    hikeRow: { display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 0.9fr', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' },
    hikeName: { fontSize: 13, fontWeight: 700, color: '#0f172a' },
    hikeLab: { fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 2 },
    hikeNums: { fontSize: 13, color: '#334155' },
    hikePct: { fontSize: 12, fontWeight: 800, marginLeft: 8 },
    hikeExtra: { fontSize: 14, fontWeight: 800, textAlign: 'right' },
    hikeSub: { fontSize: 10.5, color: '#94a3b8', fontWeight: 500, marginTop: 2 },
    marginRow: { display: 'flex', alignItems: 'stretch', gap: 8, flexWrap: 'wrap', marginTop: 4 },
    mBox: { flex: '1 1 150px', border: '1px solid #e2e8f0', borderRadius: 9, padding: '11px 13px', background: '#fbfcfd' },
    mSign: { alignSelf: 'center', fontSize: 18, fontWeight: 800, color: '#94a3b8' },
    mLine: { display: 'grid', gridTemplateColumns: '150px 1fr 58px 130px', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 },
    mName: { fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    mNums: { color: '#64748b' },
    mRate: { textAlign: 'right', fontWeight: 800 },
    mTotal: { textAlign: 'right', color: '#334155' },

    feeBox: { marginTop: 14, border: '1px solid #e2e8f0', borderRadius: 9, padding: '12px 13px', background: '#fbfcfd' },
    feeHead: { display: 'grid', gridTemplateColumns: '1fr 110px 130px 110px', gap: 8, fontSize: 11, fontWeight: 700, color: '#94a3b8', paddingBottom: 8, borderBottom: '1px solid #e2e8f0' },
    feeRow: { display: 'grid', gridTemplateColumns: '1fr 110px 130px 110px', gap: 8, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 },
    feeName: { color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    feeCost: { textAlign: 'right', color: '#64748b' },
    feeInput: { width: 110, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, textAlign: 'right', fontFamily: 'inherit' },
    feeDiff: { textAlign: 'right', fontWeight: 700 },
    feeFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12 },
    saveBtn: { padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },

    remakeBox: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
    remakeAmt: { fontSize: 24, fontWeight: 800, color: '#d03b3b', letterSpacing: '-0.5px' },
    remakePct: { fontSize: 12, color: '#64748b', fontWeight: 700 },

    srow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, color: '#334155', padding: '8px 0', borderBottom: '1px solid #f1f5f9' },
    // 💰 예상 기공비 카드 (2026-10-07)
    costHead: { display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: 8, fontSize: 11, fontWeight: 700, color: '#94a3b8', paddingBottom: 8, borderBottom: '1px solid #e2e8f0' },
    costRow: { display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: 8, alignItems: 'center', fontSize: 12.5, color: '#334155', padding: '9px 0', borderBottom: '1px solid #f1f5f9' },
    costLab: { minWidth: 0 },
    costMeta: { fontSize: 11, color: '#94a3b8', marginTop: 2, fontWeight: 500 },

    note: { marginTop: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', fontSize: 12.5, color: '#64748b', lineHeight: 1.75 },
};

export default LabCostAnalysis;
